/**
 * /evaluators — the workspace's evaluators. "+ Evaluator" opens the create
 * drawer (`?new=1`); a row opens its configure screen (`/evaluators/:id`).
 * A fresh workspace has none. Active and Archived tabs (`?view=archived`);
 * each row's ⋯ archives or restores it.
 */

import { Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { Box } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader } from "@/components/ui/page";
import { Pill } from "@/components/ui/pill";
import { EmptyState, QueryState } from "@/components/ui/states";
import { formatDate } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { CreateDrawer } from "./create-drawer";
import { useEvaluators } from "./hooks";
import { KIND } from "./labels";
import type { Evaluator } from "./types";
import { archiveColumn } from "@/features/archive/archive-menu";
import { ArchiveEmpty, ArchiveTabs } from "@/features/archive/archived";
import { useArchiveTabs } from "@/features/archive/hooks";
import { useProject } from "@/features/projects/hooks";
import { ProjectAction } from "@/features/projects/project-action";

export function EvaluatorsPage() {
  const { can } = useProject();
  const url = useUrlState();
  const navigate = useNavigate();
  const tabs = useArchiveTabs("evaluator", useEvaluators());
  const evaluators = tabs.query;
  const all = evaluators.data ?? [];
  const archived = tabs.counts.archived ?? 0;
  const open = (id: string) => navigate(`/evaluators/${id}`);
  const columns: Column<Evaluator>[] = [
    {
      header: "Name",
      cell: (e) => (
        <span className="flex flex-col gap-0.5">
          <button
            type="button"
            className="link text-left font-semibold"
            onClick={(ev) => (ev.stopPropagation(), open(e.id))}
          >
            {e.name}
          </button>
          {e.description && <span className="line-clamp-1 text-xs text-muted">{e.description}</span>}
        </span>
      ),
    },
    {
      header: "Type",
      cell: (e) => <Pill tone={KIND[e.kind]?.tone ?? "neutral"}>{KIND[e.kind]?.label ?? e.kind}</Pill>,
    },
    { header: "Model", className: "text-muted", cell: (e) => e.model ?? "—" },
    { header: "Version", className: "font-mono text-xs", cell: (e) => `v${e.version}` },
    {
      header: "Updated",
      className: "text-xs text-muted",
      cell: (e) => (e.created_at ? formatDate(e.created_at) : "—"),
    },
    archiveColumn("evaluator"),
  ];
  const add = (
    <ProjectAction permission="evaluators:write">
      <Button variant="primary" onClick={() => url.set({ new: "1" })}>
        <Plus size={14} strokeWidth={2.2} aria-hidden />
        Evaluator
      </Button>
    </ProjectAction>
  );

  return (
    <>
      <PageHeader
        title="Evaluators"
        subtitle="What an experiment can score. Every score is between 0 and 1 and comes with a written reason."
        actions={add}
      />
      <ArchiveTabs kind="evaluator" {...tabs.counts} />
      <Box>
        <QueryState query={evaluators} what="evaluators">
          {all.length === 0 && (tabs.view === "archived" || archived > 0) ? (
            <ArchiveEmpty kind="evaluator" view={tabs.view} archived={archived} create={add} />
          ) : all.length === 0 ? (
            <EmptyState title="No evaluators yet" action={add}>
              Write a prompt for an LLM judge, pick a fixed code check, or start from a template.
            </EmptyState>
          ) : (
            <DataTable columns={columns} rows={all} onRowClick={(e) => open(e.id)} />
          )}
        </QueryState>
      </Box>
      {can("evaluators:write") && url.get("new") && <CreateDrawer onClose={() => url.set({ new: null })} />}
    </>
  );
}
